import * as React from "react";
import { useState, useRef, useEffect } from "react";
import {
  Image as ImageIcon,
  FileVideo,
  X,
  Download,
  Loader2,
  Plus,
  Trash2,
  Clock,
} from "lucide-react";
import { MediaPreviewTooltip } from "../../common/MediaPreviewTooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../../ui/dialog";
import toast from "react-hot-toast";
import { useUpdateAdMutation } from "../../../services/ad";
import { cn } from "../../../lib/utils";
import { useDispatch } from "react-redux";
import { setErrorFields } from "../../../store/tableSlice";

// Updated interface to match server structure

// Utility function to download a file

// might change this back to fetch if we want to
const downloadFile = async (url, filename, _, setIsDownloading) => {
  try {
    setIsDownloading(true);

    if (!url) {
      throw new Error("Invalid URL");
    }

    const link = document.createElement("a");
    link.href = url;
    link.download = filename || extractFilenameFromUrl(url) || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("File download initiated!");
  } catch (error) {
    console.error("Download failed:", error);
    toast.error(`Failed to download file: ${error.message}`);
  } finally {
    setIsDownloading(false);
  }
};

// Update the isImageType function
const isImageType = (type) => {
  const imageTypes = ["jpg", "jpeg", "png", "gif"];
  return imageTypes.includes(type?.toLowerCase());
};

const isVideoType = (type) => {
  const videoTypes = ["mp4", "mov", "avi", "webm", "quicktime"];
  return videoTypes.includes(type?.toLowerCase());
};

const extractFilenameFromUrl = (url) => {
  try {
    // Create a URL object
    const urlObj = new URL(url);
    // Get the pathname
    const pathname = urlObj.pathname;
    // Split by '/' and get the last segment
    const filename = pathname.split("/").pop();
    // Decode the URI component to handle spaces and special characters
    return decodeURIComponent(filename);
  } catch (error) {
    console.error("Error extracting filename:", error);
    return null;
  }
};

// Media item component to handle file icons and modal functionality
const MediaItem = ({ asset, originalRow, totalFiles }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const [updateAd, { isLoading: isDeleting }] = useUpdateAdMutation();

  const getThumbnailClasses = (totalFiles) => {
    if (totalFiles === 1) {
      return "w-10 h-10 object-cover rounded shadow-sm cursor-pointer hover:opacity-90 transition-opacity";
    } else {
      return "w-8 h-8 object-cover rounded shadow-sm cursor-pointer hover:opacity-90 transition-opacity flex-shrink-0";
    }
  };

  // Handle click to open the fullscreen modal
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Don't open modal for optimistic files
    if (!asset.isOptimistic) {
      setIsModalOpen(true);
    }
  };

  // Handle file download
  const handleDownload = (e) => {
    if (!asset.isEditable || asset.isOptimistic) return;
    e.preventDefault();
    e.stopPropagation();

    if (!asset?.url) {
      toast.error("Download information is missing");
      return;
    }

    try {
      setIsDownloading(true);
      downloadFile(
        asset.url,
        asset.filename,
        setDownloadProgress,
        setIsDownloading
      );
    } catch (error) {
      console.error("Download error:", error);
      setIsDownloading(false);
      toast.error("Failed to start download");
    }
  };

  const handleDelete = async () => {
    if (!asset.isEditable || asset.isOptimistic) return;
    const payload = [
      {
        action: "remove_ad_files",
        ad_glids: [originalRow.glid.display],
        file_glids: [asset.glid],
      },
    ];
    try {
      await updateAd(payload);
      toast.success("File deleted successfully!");
      setIsModalOpen(false);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete file");
    }
  };

  const renderThumbnail = () => {
    const thumbnailClasses = getThumbnailClasses(totalFiles);

    // For optimistic updates, handle file preview differently
    if (asset.isOptimistic) {
      if (asset.type === "image") {
        return (
          <div className="relative">
            <img
              src={asset.url}
              alt={asset.filename}
              className={cn(thumbnailClasses, "opacity-50")}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
            </div>
          </div>
        );
      } else if (asset.type === "video") {
        return (
          <div className="relative">
            <div
              className={cn(
                thumbnailClasses,
                "bg-gray-100 flex items-center justify-center opacity-50"
              )}
            >
              <FileVideo className="w-6 h-6 text-gray-400" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
            </div>
          </div>
        );
      }
    }

    // Regular file rendering
    if (isImageType(asset.type)) {
      return (
        <img
          src={asset.url}
          alt={asset.filename}
          className={thumbnailClasses}
        />
      );
    } else if (isVideoType(asset.type)) {
      return (
        <div className="relative">
          <VideoThumbnail videoUrl={asset.url} className={thumbnailClasses} />
          {/* Play icon overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 rounded">
            <div className="w-4 h-4 flex items-center justify-center">
              <div className="w-0 h-0 border-l-[6px] border-l-white border-y-[4px] border-y-transparent" />
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div
          className={`${thumbnailClasses} bg-gray-100 flex items-center justify-center`}
        >
          <FileVideo className="w-6 h-6 text-gray-400" />
        </div>
      );
    }
  };

  return (
    <>
      <div className="inline-flex relative group media-item flex-shrink-0">
        <MediaPreviewTooltip
          type={asset.type}
          fileName={asset.filename}
          title={asset.title || asset.filename}
          mediaSrc={asset.url}
          duration={asset.duration}
        >
          <div onClick={handleClick}>{renderThumbnail()}</div>
        </MediaPreviewTooltip>
      </div>

      {/* Only render modal for non-optimistic files */}
      {!asset.isOptimistic && (
        <Dialog
          open={isModalOpen}
          onOpenChange={(open) => {
            setIsModalOpen(open);
            if (!open) {
              setTimeout(() => {
                document.activeElement?.blur();
              }, 0);
            }
          }}
        >
          <DialogContent className="max-w-6xl w-full h-[90vh] p-0 overflow-hidden rounded-xl shadow-2xl border-0">
            {/* Enhanced Header */}
            <DialogHeader className="flex flex-row justify-between items-center p-4 border-b bg-white">
              <div className="flex items-center gap-3">
                {isImageType(asset.type) ? (
                  <ImageIcon className="text-blue-600 w-5 h-5" />
                ) : (
                  <FileVideo className="text-blue-600 w-5 h-5" />
                )}
                <DialogTitle className="text-lg font-medium text-gray-800 m-0">
                  {asset.title || asset.filename}
                </DialogTitle>
              </div>
              <div className="flex items-center gap-2">
                {/* Header Download Button */}
                <button
                  onClick={(e) => handleDownload(e)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5 text-gray-600" />
                  )}
                </button>
                {asset.isEditable && (
                  <button
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
                    ) : (
                      <Trash2 className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                )}
                <DialogClose className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200">
                  <X className="w-5 h-5 text-gray-600" />
                </DialogClose>
              </div>
            </DialogHeader>

            {/* Media Content Area */}
            <div className="flex-1 flex items-center justify-center bg-gray-900 p-0 relative overflow-hidden">
              {isImageType(asset.type) ? (
                <div className="relative w-full h-full flex items-center justify-center">
                  <img
                    src={asset.url}
                    alt={asset.alt || asset.title || "Image preview"}
                    className="max-h-full max-w-full object-contain transition-all duration-300 ease-in-out"
                  />
                </div>
              ) : isVideoType(asset.type) ? (
                <div className="relative w-full h-full flex items-center justify-center">
                  <video
                    src={asset.url}
                    className="max-h-full max-w-full shadow-lg"
                    controls
                    playsInline
                    preload="metadata"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <span className="text-white">Unsupported file type</span>
                </div>
              )}
            </div>

            {/* Enhanced Footer */}
            <div className="p-4 bg-white border-t">
              <div className="flex justify-between items-center">
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-900">
                    {asset.filename}
                  </span>
                  <div className="flex items-center mt-1 space-x-4">
                    {asset.type === "video" && asset.duration && (
                      <span className="text-xs text-gray-500 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {asset.duration}
                      </span>
                    )}
                    {asset.size && (
                      <span className="text-xs text-gray-500">
                        {typeof asset.size === "number"
                          ? `${(asset.size / (1024 * 1024)).toFixed(2)} MB`
                          : asset.size}
                      </span>
                    )}
                    {asset.dimensions && (
                      <span className="text-xs text-gray-500">
                        {asset.dimensions}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export const FilesColumnCell = ({
  row,
  data = { files: [], limit: 3 },
  onUpdate,
  isLoading,
  columnInfo,
  isEditable,
  isError,
  isRequired,
  isEmpty: isEmptyProp,
  errorFields,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [optimisticFiles, setOptimisticFiles] = useState(data.files);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const dispatch = useDispatch();

  // Update optimistic files when data changes
  useEffect(() => {
    setOptimisticFiles(data.files);
  }, [data.files]);

  // Determine if the field is actually empty based on optimistic files
  const isEmpty = optimisticFiles.length === 0;

  // Handle cell click to open file input
  const handleCellClick = (e) => {
    e.stopPropagation();
    // Prevent opening if any existing media items were clicked
    if (e.target.closest(".media-item") || e.target.closest('[role="dialog"]'))
      return;
    fileInputRef.current?.click();
  };

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (dragCounter.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const files = e.dataTransfer.files;
    handleFiles(files);
  };

  // Process selected or dropped files
  const handleFiles = (files) => {
    if (!files || files.length === 0) return;

    if (optimisticFiles.length + files.length > data.limit) {
      toast.error(`You can only upload ${data.limit} files`);
      return;
    }

    const allowedTypes = {
      image: ["image/jpeg", "image/jpg", "image/png", "image/gif"],
      video: [
        "video/mp4",
        "video/quicktime",
        "video/x-msvideo",
        "video/webm",
        "video/avi",
        "video/mov",
      ],
    };

    const isAllowedType = (file) => {
      return [...allowedTypes.image, ...allowedTypes.video].includes(file.type);
    };

    for (let file of files) {
      if (!isAllowedType(file)) {
        toast.error(`${file.name} is not a supported file type`);
        return; // Stop immediately if any file is invalid
      }
    }

    // Create optimistic file objects
    const newOptimisticFiles = Array.from(files).map((file) => ({
      filename: file.name,
      type: file.type.startsWith("image/") ? "image" : "video",
      size: file.size,
      // Create a temporary URL for preview
      url: URL.createObjectURL(file),
      isOptimistic: true,
    }));

    // Update optimistic state immediately
    setOptimisticFiles((prev) => [...prev, ...newOptimisticFiles]);

    // If we reach here, all files are valid
    if (onUpdate) {
      onUpdate(files)
        .then(() => {
          // Clear error state on successful upload
          const updatedErrorFields = errorFields
            .map((error) => {
              if (error.rowId === row.id) {
                const filteredFields = error.fields.filter(
                  (field) => field !== columnInfo.key
                );
                return { ...error, fields: filteredFields };
              }
              return error;
            })
            .filter((error) => error.fields.length > 0);

          dispatch(setErrorFields(updatedErrorFields));

          // Clean up temporary URLs
          newOptimisticFiles.forEach((file) => {
            if (file.isOptimistic) {
              URL.revokeObjectURL(file.url);
            }
          });
        })
        .catch((error) => {
          console.error("File upload error:", error);
          // Revert optimistic update on error
          setOptimisticFiles(data.files);

          // Update error state if required and empty
          if (isRequired && data.files.length === 0) {
            const existingError = errorFields.find(
              (error) => error.rowId === row.id
            );
            if (existingError) {
              const updatedFields = Array.from(
                new Set([...existingError.fields, columnInfo.key])
              );
              dispatch(
                setErrorFields(
                  errorFields.map((error) =>
                    error.rowId === row.id
                      ? { ...error, fields: updatedFields }
                      : error
                  )
                )
              );
            } else {
              dispatch(
                setErrorFields([
                  ...errorFields,
                  {
                    rowId: row.id,
                    fields: [columnInfo.key],
                  },
                ])
              );
            }
          }
        });
    }
  };

  return (
    <div
      className={cn(
        "relative w-full h-full min-h-[40px] flex items-center min-w-0",
        isEditable && "cursor-pointer",
        isError && isEmpty && "border border-red-500 bg-red-50"
      )}
      onClick={isEditable ? handleCellClick : undefined}
      onMouseEnter={isEditable ? () => setIsHovered(true) : undefined}
      onMouseLeave={isEditable ? () => setIsHovered(false) : undefined}
      onDragEnter={isEditable ? handleDragEnter : undefined}
      onDragLeave={isEditable ? handleDragLeave : undefined}
      onDragOver={isEditable ? handleDragOver : undefined}
      onDrop={isEditable ? handleDrop : undefined}
    >
      {isRequired && isEmpty && (
        <span
          className="text-red-500 mr-1 align-middle"
          title="This is a required field."
        >
          *
        </span>
      )}
      {/* Drag and Drop Overlay */}
      {isEditable && isDragging && (
        <div className="absolute inset-0 z-50 bg-blue-500 bg-opacity-20 border-2 border-blue-500 border-dashed flex items-center justify-center">
          <span className="text-blue-700 font-medium">Drop file here</span>
        </div>
      )}

      {/* Plus Icon */}
      {isEditable && data.files.length < (data.limit || 3) && (
        <div className="flex-shrink-0">
          <div
            className={`
              flex items-center justify-center 
              ${isHovered ? "opacity-100" : "opacity-0 group-hover:opacity-100"}
              transition-opacity duration-200
            `}
          >
            <button
              className="
                p-1 rounded-full 
                bg-blue-500 hover:bg-blue-600 
                flex items-center justify-center
                focus:outline-none focus:ring-2 focus:ring-blue-300
              "
              onClick={handleCellClick}
            >
              <Plus className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}

      {/* Existing Media Items */}
      <div className="flex items-center gap-1 overflow-hidden">
        {optimisticFiles.length < 1 && (
          <span className="text-gray-400 flex-shrink-0">
            {columnInfo.display}
          </span>
        )}
        {optimisticFiles.map((file, index) => (
          <MediaItem
            originalRow={row.original}
            key={`${file.type}-${index}`}
            asset={{ ...file, isEditable }}
            totalFiles={optimisticFiles.length}
            className="media-item flex-shrink-0"
          />
        ))}
        {isLoading && (
          <div className="inline-flex flex-shrink-0">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      {isEditable && (
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/jpeg,image/png,image/gif,video/mp4,video/quicktime,video/x-msvideo"
          multiple={data.limit > 1}
          onClick={(e) => {
            // Reset the input value to ensure onChange fires even if selecting the same file
            e.target.value = "";
          }}
          onChange={(e) => {
            e.stopPropagation();
            handleFiles(e.target.files);
          }}
        />
      )}
    </div>
  );
};

const VideoThumbnail = ({ videoUrl, className }) => {
  const [thumbnail, setThumbnail] = useState("");
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;

    const generateThumbnail = () => {
      // Create a canvas to capture the video frame
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas
        .getContext("2d")
        .drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to data URL
      const thumbnailUrl = canvas.toDataURL();
      setThumbnail(thumbnailUrl);
    };

    // Set up video element
    video.addEventListener("loadeddata", generateThumbnail);
    video.addEventListener("error", () => {
      console.error("Error loading video for thumbnail");
    });

    // Seek to the first frame
    video.currentTime = 0;

    return () => {
      video.removeEventListener("loadeddata", generateThumbnail);
    };
  }, [videoUrl]);

  return (
    <>
      <video
        ref={videoRef}
        src={videoUrl}
        style={{ display: "none" }}
        preload="metadata"
      />
      {thumbnail ? (
        <img src={thumbnail} alt="Video thumbnail" className={className} />
      ) : (
        <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded">
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        </div>
      )}
    </>
  );
};
